import React, { useState, useEffect } from 'react';
import { Plus, Phone, Mail, MapPin, Hammer } from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { getApiUrl } from '../../config/api';

export default function FarrierWidget({ config = {}, onConfigChange }) {
  const [farriers, setFarriers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const {
    title = 'Primary Farrier',
    showClinic = true,
    showEmail = false,
    selectedFarrierId = null
  } = config;

  // Find the selected farrier from the config
  const selectedFarrier = farriers.find(farrier => farrier.id === selectedFarrierId);

  useEffect(() => {
    fetchFarriers();
  }, []);

  const fetchFarriers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${getApiUrl('/api/farriers')}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setFarriers(response.data);
      
      // Set the first farrier as selected by default if none is selected
      if (response.data.length > 0 && !selectedFarrierId && onConfigChange) {
        onConfigChange({
          ...config,
          selectedFarrierId: response.data[0].id
        });
      }
      
      setError(null);
    } catch (err) {
      console.error('Error fetching farriers:', err);
      setError('Failed to fetch farriers');
    } finally {
      setLoading(false);
    }
  };



  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <Hammer className="mr-2 h-5 w-5 text-blue-600" />
            {title}
          </h3>
          <div className="flex items-center space-x-2">
          </div>
        </div>
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <Hammer className="mr-2 h-5 w-5 text-blue-600" />
            {title}
          </h3>
          <div className="flex items-center space-x-2">
          </div>
        </div>
        <div className="text-center text-red-600">
          <p className="mb-2">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow p-4">
      <div className="flex items-center gap-2 mb-3">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center">
          <Hammer className="mr-2 h-5 w-5 text-blue-600" />
          {title}
        </h3>
      </div>

      {farriers.length === 0 ? (
        <div className="text-center py-8">
          <Hammer className="mx-auto h-12 w-12 text-gray-400 mb-3" />
          <p className="text-gray-500 mb-3">No farriers added yet</p>
          <Link
            to="/farriers"
            className="inline-flex items-center px-3 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700"
          >
            <Plus className="mr-1 h-4 w-4" />
            Add Farrier
          </Link>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Selected Farrier Display */}
          {selectedFarrier ? (
                         <div className="bg-gray-50 rounded-lg p-4 space-y-3">
               <div className="flex items-center gap-2">
                 <Hammer className="h-4 w-4 text-blue-600" />
                 <span className="font-medium text-gray-900">{selectedFarrier.name}</span>
               </div>
              
              {selectedFarrier.phone && (
                <div className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-gray-500" />
                  <span className="text-sm text-gray-700">{selectedFarrier.phone}</span>
                </div>
              )}

              {showClinic && selectedFarrier.clinic_name && (
                <div className="text-sm text-gray-600">
                  <span className="font-medium">Clinic:</span> {selectedFarrier.clinic_name}
                </div>
              )}

              {showEmail && selectedFarrier.email && (
                <div className="text-sm text-gray-600">
                  <span className="font-medium">Email:</span> {selectedFarrier.email}
                </div>
              )}

              {selectedFarrier.address && (
                <div className="text-sm text-gray-600">
                  <span className="font-medium">Address:</span> {selectedFarrier.address}
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-4">
              <p className="text-gray-500 text-sm">No farrier selected</p>
              <p className="text-gray-400 text-xs mt-1">Configure widget to select a farrier</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
